import { useCallback } from "react";
import axios, { AxiosResponse } from "axios";
import { AuthenticationResult, BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "../../../authConfig";
import { useAccount, useMsal } from "@azure/msal-react";
import { useGlobalContext } from "../../GlobalContext";

interface clientOptions {
  requireAuth?: boolean;
  identity?: boolean;
  cusotmUrl?: string;
}

function useApiClient(controller: string, { requireAuth = true, identity = false, cusotmUrl }: clientOptions = {}): apiClient {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const routeUrlApi = `${import.meta.env.VITE_API_KEY}/api/${controller}`;
  const routeUrlIdentity = `${import.meta.env.VITE_IDENTITY}/api/${controller}`;
  const { addLoadingUrl, removeLoadedUrl } = useGlobalContext();

  const getAccessToken = useCallback(async () => {
    if (!account) {
      throw new Error("No account available");
    }
    const accessTokenRequest = {
      scopes: loginRequest.scopes,
      account: account,
    };
    try {
      addLoadingUrl("msal");
      return await instance
        .acquireTokenSilent(accessTokenRequest)
        .catch(async (error) => {
          if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
            if (account && (inProgress === "none" || inProgress === "startup")) {
              return await instance.acquireTokenRedirect({
                scopes: loginRequest.scopes,
                state: location.pathname,
              });
            }
          }
        })
        .finally(async () => {
          removeLoadedUrl("msal");
        });
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
        if (account && inProgress === "none") {
          return await instance.acquireTokenRedirect({
            scopes: loginRequest.scopes,
            state: location.pathname,
          });
        }
      }
      throw error;
    }
  }, [instance, account, inProgress]);

  const handleCall = useCallback(async (apiCall: Promise<AxiosResponse<any, any>>, apiEndpoint: string): Promise<AxiosResponse<any, any>> => {
    try {
      addLoadingUrl(apiEndpoint);
      const response = await apiCall;
      return response;
    } finally {
      removeLoadedUrl(apiEndpoint);
    }
  }, []);

  const fetchData = useCallback(
    async (route?: string, params?: any): Promise<AxiosResponse<any, any>> => {
      const apiEndpoint = (identity ? routeUrlIdentity : (cusotmUrl ?? routeUrlApi)) + (route ? `/${route}` : "");

      let headers = {};
      if (requireAuth) {
        const token = (await getAccessToken()) as AuthenticationResult;
        headers = { Authorization: `Bearer ${token?.accessToken}` };
      }
      return handleCall(axios.get(apiEndpoint, { headers, params }), apiEndpoint);
    },
    [routeUrlApi, getAccessToken, handleCall]
  );

  const dateTransformer = (data: any): any => {
    if (data !== "" && data !== undefined) {
      if (data instanceof Date && data !== null) {
        const transformedDate = data.keepTime ? data : new Date(Date.UTC(data.getFullYear(), data.getMonth(), data.getDate())).toISOString();
        return transformedDate;
      }
      if (Array.isArray(data)) {
        return data.map(dateTransformer);
      }
      if (typeof data === "object" && data !== null) {
        return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, dateTransformer(value)]));
      }
    }
    return data;
  };

  const postData = useCallback(
    async (data: any, route?: string, params?: any): Promise<AxiosResponse<any, any>> => {
      const apiEndpoint = (identity ? routeUrlIdentity : (cusotmUrl ?? routeUrlApi)) + (route ? `/${route}` : "");
      let headers = {};
      if (requireAuth) {
        const token = (await getAccessToken()) as AuthenticationResult;
        headers = { Authorization: `Bearer ${token?.accessToken}`, "Content-Type": "application/json" };
      }
      return handleCall(
        axios
          .post(apiEndpoint, data, {
            headers,
            transformRequest: [
              (data) => {
                return JSON.stringify(dateTransformer(data));
              },
            ],
            params,
          })
          .then((res) => {
            return res;
          }),
        apiEndpoint
      );
    },
    [routeUrlApi, getAccessToken, handleCall]
  );

  const putData = useCallback(
    async (data: any, route?: string, params?: any): Promise<AxiosResponse<any, any>> => {
      const apiEndpoint = (identity ? routeUrlIdentity : (cusotmUrl ?? routeUrlApi)) + (route ? `/${route}` : "");
      let headers = {};
      if (requireAuth) {
        const token = (await getAccessToken()) as AuthenticationResult;
        headers = { Authorization: `Bearer ${token?.accessToken}`, "Content-Type": "application/json" };
      }
      return handleCall(
        axios
          .put(apiEndpoint, data, {
            headers,
            transformRequest: [
              (data) => {
                return JSON.stringify(dateTransformer(data));
              },
            ],
            params,
          })
          .then((res) => {
            return res;
          }),
        apiEndpoint
      );
    },
    [routeUrlApi, getAccessToken, handleCall]
  );

  return {
    fetchData,
    postData,
    putData,
  };
}

export interface apiClient {
  fetchData: (route?: string, params?: any) => Promise<AxiosResponse<any, any>>;
  postData: (data: any, route?: string, params?: any) => Promise<AxiosResponse<any, any>>;
  putData: (data: any, route?: string, params?: any) => Promise<AxiosResponse<any, any>>;
}
export default useApiClient;
